<script lang="ts" setup>
import { useErrorHandler } from '@errors/useErrorHandler';
import { useDomeinVerificatieSendDnsRecordInstructions } from '@services/crm-public/orval-api/domein-verificatie/domein-verificatie';
import type { CrmDomeinVerificatieGetDomeinenPerAdviseurResponse as Domein } from '@services/crm-public/orval-api/model/crmDomeinVerificatieGetDomeinenPerAdviseurResponse';
import VButton from '@shared/component/VButton/VButton.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VStyledHtml from '@shared/component/VStyledHtml/VStyledHtml.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import VInput from '@shared/form/VInput/VInput.vue';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { computed, ref } from 'vue';
import { z } from 'zod';

defineProps<{
  domeinen?: Domein[];
  isPending: boolean;
}>();

const hasSubmitted = ref(false);
const emailFieldId = 'dns-instructions-email';
const emailadresMaxLength = 63;

type InstructionMailForm = {
  emailadres: string;
};

const { defineField, errors, handleSubmit, meta, resetForm } = useForm<InstructionMailForm>({
  validationSchema: toTypedSchema(
    z.object({
      emailadres: z
        .string()
        .trim()
        .min(1, { message: 'Vul een e-mailadres in.' })
        .email({ message: 'Vul een geldig e-mailadres in.' })
        .max(emailadresMaxLength),
    }),
  ),
  initialValues: {
    emailadres: '',
  },
});
const [emailadres] = defineField('emailadres');

const notification = useNotification();
const { handleError } = useErrorHandler();
const sendInstructions = useDomeinVerificatieSendDnsRecordInstructions();

const trimmedEmailadres = computed(() => (emailadres.value ?? '').trim());
const emailError = computed(() => (hasSubmitted.value ? errors.value.emailadres : undefined));
const canSubmit = computed(
  () => !!trimmedEmailadres.value && meta.value.valid && !sendInstructions.isPending.value,
);

const sendInstructionMail = handleSubmit(async (values) => {
  const emailadresToSend = values.emailadres.trim();

  try {
    await sendInstructions.mutateAsync({ data: emailadresToSend });
    notification.success(`De instructie is verstuurd naar ${emailadresToSend}.`);
    resetForm();
    hasSubmitted.value = false;
  } catch (submitError) {
    handleError(submitError instanceof Error ? submitError : null);
  }
});

async function submitInstructionMail() {
  hasSubmitted.value = true;

  await sendInstructionMail();
}
</script>

<template>
  <div v-if="isPending" class="flex max-w-readable-text flex-col gap-6">
    <VSkeleton :count="3" />
    <VSkeleton :count="2" />
    <VSkeleton class="max-w-60" />
    <VSkeleton :count="3" />
  </div>

  <VStyledHtml v-else role="article">
    <p>
      Wanneer SUREnet e-mails namens jouw organisatie verstuurt, is het belangrijk dat ontvangers
      kunnen zien dat deze e-mails betrouwbaar zijn.
    </p>

    <p>Als deze instellingen niet goed zijn ingericht, bestaat de kans dat e-mails:</p>

    <ul>
      <li>in de map Ongewenste e-mail (spam) terechtkomen;</li>
      <li>niet worden afgeleverd;</li>
      <li>of volledig worden geweigerd.</li>
    </ul>

    <p>
      Door de juiste instellingen toe te voegen, weten ontvangende e-mailservers dat SUREnet
      toestemming heeft om namens jouw organisatie e-mails te versturen.
    </p>

    <h2>Wat moet er gebeuren?</h2>

    <p>
      Om dit mogelijk te maken, moeten twee beveiligingsinstellingen worden toegevoegd aan de
      instellingen van jullie internetdomein (bijvoorbeeld bedrijfsnaam.nl).
    </p>

    <ul>
      <li>
        <strong>Sender Policy Framework (SPF)</strong>
        <p>
          SPF is een beveiligingsinstelling die aangeeft welke systemen e-mails mogen versturen
          namens jouw organisatie. Voor een correcte werking moet SUREbusiness worden toegevoegd aan
          deze lijst met toegestane verzenders.
        </p>

        <ul v-if="domeinen?.length">
          <li v-for="domein in domeinen" :key="domein.domein">
            <span>Include:</span> <code>include:spf.{{ domein.domein }}</code>
          </li>
        </ul>
      </li>

      <li>
        <strong>DomainKeys Identified Mail (DKIM)</strong>
        <p>
          DKIM is een beveiligingsmethode waarbij een digitale handtekening aan iedere verzonden
          e-mail wordt toegevoegd.
        </p>

        <p>Hierdoor kunnen ontvangende e-mailservers controleren dat:</p>

        <ul>
          <li>de e-mail daadwerkelijk namens jouw organisatie is verzonden;</li>
          <li>en dat de inhoud van de e-mail onderweg niet is gewijzigd.</li>
        </ul>
      </li>
    </ul>

    <h2>Wie voert dit uit?</h2>

    <p>Deze instellingen worden meestal beheerd door:</p>

    <ul>
      <li>jullie IT-afdeling;</li>
      <li>systeembeheerder;</li>
      <li>of de leverancier die het internetdomein en de e-mail beheert.</li>
    </ul>

    <p>
      Ben je hier zelf niet verantwoordelijk voor? Stuur onder Technische handleiding de technische
      handleiding door naar degene die dit beheert.
    </p>

    <h2>Waar moeten de instellingen worden toegevoegd?</h2>

    <p>
      De instellingen worden toegevoegd bij de partij waar het Domain Name System (DNS) van jullie
      internetdomein wordt beheerd. Het DNS zorgt ervoor dat een internetdomein, zoals
      bedrijfsnaam.nl, op de juiste manier werkt.
    </p>

    <p>Hier worden onder andere de instellingen voor e-mail vastgelegd.</p>

    <p>
      In de technische handleiding staat precies beschreven welke instellingen moeten worden
      toegevoegd, waaronder:
    </p>

    <ul>
      <li>het SPF-record;</li>
      <li>en de Canonical Name (CNAME)-records die nodig zijn voor DKIM.</li>
    </ul>

    <h2>Technische handleiding</h2>

    <p>
      We hebben een duidelijke handleiding gemaakt waarin stap voor stap staat beschreven welke
      instellingen moeten worden toegevoegd. Deze handleiding is bedoeld voor de IT-beheerder of
      leverancier die het internetdomein beheert.
    </p>

    <form class="mt-6" @submit.prevent="submitInstructionMail">
      <FieldRowV2
        :errors="[emailError]"
        label="Technische handleiding sturen naar:"
        :label-for="emailFieldId"
        :label-size="1"
      >
        <VInput
          :id="emailFieldId"
          v-model="emailadres"
          class="w-full min-w-0 sm:w-64 sm:grow-0"
          :has-error="!!emailError"
          :is-disabled="sendInstructions.isPending.value"
          :max-length="emailadresMaxLength"
          placeholder="info@surebusiness.nl"
          type="email"
        />

        <VButton
          class="shrink-0"
          :disabled="!canSubmit"
          :loading="sendInstructions.isPending.value"
          submit
        >
          Verstuur
        </VButton>
      </FieldRowV2>
    </form>

    <h2>Wat gebeurt er daarna?</h2>

    <p>Nadat de instellingen zijn toegevoegd:</p>

    <ul>
      <li>zijn de wijzigingen meestal binnen enkele minuten actief;</li>
      <li>kan het in sommige gevallen tot 24 uur duren voordat ze wereldwijd zijn verwerkt.</li>
    </ul>

    <p>SUREnet controleert vervolgens automatisch of alle instellingen correct zijn ingericht.</p>

    <p>Hieronder zie je bij Actuele statussen per onderdeel of alles succesvol is ingesteld.</p>
  </VStyledHtml>
</template>
